<script setup>
import { computed, ref } from 'vue';
import { asset } from '../../helpers.js';

const props = defineProps({
    startAt: { type: String, required: true },
    endAt: { type: String, required: true },
    minOrderValue: { type: Number, required: true },
    landingPage: { type: Boolean, default: true, required: false }
});

const imgPHPointer = asset('/hkcm/images/PHPointer.png', 'public');
const imgPreisPraesentation = asset('/hkcm/images/PreisPraesentation.png', 'public');

const copied = ref(false);

function copyCode() {
    navigator.clipboard.writeText('GLUECK20').then(() => {
        copied.value = true;
        setTimeout(() => { copied.value = false; }, 2000);
    });
}

const expandedSofort = ref(new Set());
const toggleSofort = (title) => {
    expandedSofort.value.has(title) ? expandedSofort.value.delete(title) : expandedSofort.value.add(title);
    expandedSofort.value = new Set(expandedSofort.value);
};

function formatOrderValue(value) {
    return Math.round(value).toLocaleString('de-DE');
}

const sofortgewinne = [
    { img: asset('/hkcm/images/Preis_1_Hyundaii10.png', 'public'), title: 'Hyundai i10', desc: 'Das perfekte Stadtauto. Zuverlässig. Flexibel. Nagelneu!' },
    { img: asset('/hkcm/images/Preis_2_Lifetimeabo.png', 'public'), title: 'Lifetime HKCM-Abo', desc: 'Das exklusive HKCM-Lifetime-Abo im Wert von 20.000 €. Alle Märkte. Alle Analysen. Für immer!' },
    { img: asset('/hkcm/images/Preis_3_3000Cash.png', 'public'), title: '3.000 € Cash', desc: '3.000 Euro Cash in der Tasch. Gönnen Sie sich damit endlich Ihren langersehnten Wunsch!' },
    { img: asset('/hkcm/images/Preis_4_Goldmuenze.png', 'public'), title: 'Goldmünze', desc: 'Werden Sie zum Goldfinger mit einer Münze aus purem Gold. Perfekt als Anlage oder zum Verschenken.' },
    { img: asset('/hkcm/images/Preis_5_1000Cash.png', 'public'), title: '1.000 € Cash', desc: 'Es gibt 1000 gute Gründe, diesen Preis auf den Kopf zu hauen. Welcher ist Ihrer?' },
    { img: asset('/hkcm/images/Preis_6_Silbermuenze4oz.png', 'public'), title: 'Silbermünze 4oz', desc: 'Hilft im Geldbeutel und gegen Werwölfe. Gewinnen Sie eine Münze aus purem Silber!' },
    {
        img: asset('/hkcm/images/Preis_7_Silbermuenze1oz.png', 'public'),
        title: 'Silbermünze 1oz',
        desc: 'Der kleine Bruder der 4oz Münze. Handlich, praktisch, wertvoll.',
        imgClass: 'h-[120px] sm:self-auto sm:w-[140px] sm:h-[140px] scale-[0.65]'
    },
    {
        img: asset('/hkcm/images/Preis_8_3Monatsabo.png', 'public'),
        title: '3-Monatsabo Ihrer Wahl',
        desc: "Ein 3-Monatsabo der HKCM Ihrer Wahl. Suchen Sie sich ihr Paket aus, egal ob Aktien, Indizes, Krypto oder Rohstoffe, drei Monate lang gibt's unsere Analysen für lau. Der Gutschein kann zur Verlängerung eines bestehenden Abos oder für ein neues 3-Monatsabo genutzt werden."
    }
];

const hauptpreise = [
    {
        img: asset('/hkcm/images/Hauptpreis_1_malediven.png', 'public'),
        title: 'Luxusreise auf die Malediven für 2 Personen',
        desc: '5-Sterne-Hotel & Malediven-Feeling pur. Sie und eine Begleitperson erleben eines der exklusivsten Reiseziele der Welt. Freuen Sie sich auf weiße Strände, Ruhe, Luxus und unvergessliche Momente auf diesem Inselparadies.'
    },
    {
        img: asset('/hkcm/images/Hauptpreis_2_Monaco.png', 'public'),
        title: 'Luxuswochenende in Monaco für 2 Personen',
        desc: "Yachthafen, Casino, Luxushotels und James-Bond-Flair: Sie und eine Begleitperson erleben Monaco von seiner exklusivsten Seite. Freuen Sie sich auf ein Wochenende voller Glamour, Côte d'Azur und purem Luxus."
    },
    {
        img: asset('/hkcm/images/Hauptpreis_3_Athen.png', 'public'),
        title: 'Exklusive Athen-Reise für 2 Personen',
        desc: 'Mediterranes Flair, antike Geschichte und griechischer Genuss: Sie und eine Begleitperson erleben eine der faszinierendsten Städte Europas. Freuen Sie sich auf beeindruckende Kultur, besondere Kulinarik und unvergessliche Momente zwischen Akropolis, Altstadt und Ägäis.'
    },
    {
        img: asset('/hkcm/images/Hauptpreis_4_PH.png', 'public'),
        title: 'Wine & Dine Event mit Philip Hopf in Zürich',
        desc: 'Ein exklusives Luxus Wine & Dine Event mit Philip Hopf in Zürich für 2 Personen – inklusive Hotel und Anreise für einen Tag. Freuen Sie sich auf persönliche Gespräche, besondere Einblicke und einen außergewöhnlichen Abend am Fuße der Schweizer Alpen.'
    },
    {
        img: asset('/hkcm/images/Hauptpreis_5_PK.png', 'public'),
        title: 'Adrenalin-Experience Nordschleife',
        desc: 'Motorsport, Tempo & pure Gänsehaut: Philip Klinkmüller nimmt Sie mit auf die legendäre Nordschleife. Freuen Sie sich auf Adrenalin, Geschwindigkeit und ein Erlebnis, das garantiert nicht auf der Strecke bleibt.'
    },
    {
        img: asset('/hkcm/images/Hauptpreis_6_Phantom.png', 'public'),
        title: 'Exklusiver Phantom Call',
        desc: 'Ein 60-minütiger Call mit dem legendären HKCM Phantom: Stellen Sie Ihre Fragen rund um Aktien, Krypto und mehr. Freuen Sie sich auf Insider-Tipps, Expertenwissen und persönliche Einschätzungen.'
    }
];

const steps = computed(() => [
    {
        title: `Für min. ${formatOrderValue(props.minOrderValue)} € einkaufen`,
        subtitle: `Legen Sie HKCM-Produkte im Wert von mindestens ${formatOrderValue(props.minOrderValue)} € in den Warenkorb und schließen Sie die Bestellung ab.`,
        icon: 'cart',
        highlight: true
    },
    {
        title: 'Glücksrad drehen',
        subtitle: 'Nach dem Checkout erscheint das Glücksrad, Sie können sofort teilnehmen.',
        icon: 'spin',
        highlight: !props.landingPage
    },
    {
        title: 'Sofort gewinnen',
        subtitle: 'Nach dem Dreh gewinnen Sie sofort einen unserer exklusiven Preise.',
        icon: 'trophy',
        highlight: false
    },
    {
        title: 'Hauptpreis-Auslosung',
        subtitle: 'Am Ende des Kampagnenzeitraums werden die Gewinner der Hauptpreise ausgelost und verkündet.',
        icon: 'ticket',
        highlight: false
    }
]);
</script>

<template>
    <div class="w-full">
        <!-- Rabatt-Banner -->
        <div
            class="relative rounded-xl mx-1 sm:mx-0 mb-10 sm:mb-14 flex flex-row items-stretch w-full overflow-visible"
            style="background: rgba(8, 18, 55, 0.82); border: 1px solid rgba(255, 255, 255, 0.25)"
        >
            <!-- Text -->
            <div class="flex flex-col justify-center gap-3 py-5 px-5 flex-1 min-w-0 sm:pr-[240px]">
                <p style="font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 18px; line-height: 130%; color: #dcc149">
                    Exklusiver 20&nbsp;% Zusatzrabatt auf alle Produkte mit dem Code: <span style="white-space: nowrap">GLUECK20</span>
                </p>
                <p style="font-family: 'Montserrat', sans-serif; font-weight: 400; font-size: 17px; line-height: 150%; color: #fff">
                    Während des Aktionszeitraums erhalten alle Käufer unserer Produkte zusätzlich 20&nbsp;% Rabatt auf das gesamte HKCM-Sortiment, inklusive Videoanalysen.<br />
                    Der Rabatt gilt ausschließlich bei Eingabe des Rabatt-Codes
                    <button
                        class="group inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg transition-all duration-200 hover:scale-[1.01] active:scale-95 align-middle"
                        style="background: rgba(220, 193, 73, 0.15); border: 1px solid rgba(220, 193, 73, 0.5); box-shadow: 0 2px 8px rgba(220, 193, 73, 0.15)"
                        @click="copyCode"
                    >
                        <span style="font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 16px; color: #dcc149; letter-spacing: 2px">GLUECK20</span>
                        <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#DCC149" viewBox="0 0 256 256" class="opacity-80 group-hover:opacity-100 transition-opacity">
                            <path d="M216,32H88a8,8,0,0,0-8,8V80H40a8,8,0,0,0-8,8V216a8,8,0,0,0,8,8H168a8,8,0,0,0,8-8V176h40a8,8,0,0,0,8-8V40A8,8,0,0,0,216,32ZM160,208H48V96H160Zm48-48H176V88a8,8,0,0,0-8-8H96V48H208Z"></path>
                        </svg>
                        <span v-else style="font-size: 11px; color: #4ade80; font-weight: 600">✓</span>
                    </button>
                    im Checkout. Der Code ist nicht automatisch hinterlegt und nur während des Aktionszeitraums (28.05.–08.06.2026) gültig.
                </p>
            </div>

            <!-- PH Pointer rechts -->
            <img
                :src="imgPHPointer"
                alt=""
                aria-hidden="true"
                class="absolute right-3 object-contain pointer-events-none hidden sm:block rotate-[-3deg]"
                style="width: 220px; height: auto; bottom: -10px; filter: drop-shadow(10px 14px 14px rgba(0, 0, 0, 0.35))"
            />
        </div>
        <!-- Preise -->
        <section
            class="flex flex-col 2xl:flex-row items-start justify-center 2xl:justify-between gap-8 sm:gap-12 overflow-visible"
            :class="props.landingPage ? 'mt-10 sm:mt-14' : 'mt-0'"
        >
            <!-- Glücksrad-Gewinne (BLAU) -->
            <div class="flex flex-col items-stretch gap-4 w-full 2xl:w-1/2 2xl:min-w-0">
                <div v-if="props.landingPage" class="hidden 2xl:flex h-[300px] items-center justify-center">
                    <img :src="$asset('/hkcm/images/wheelTitle.png', 'public')" alt="Das HKCM-Glücksrad" class="w-auto max-w-[400px] object-contain" />
                </div>
                <div class="w-full rounded-3xl p-[2px] bg-gradient-to-br from-blue-400/90 via-blue-500/70 to-indigo-700 shadow-[0_20px_60px_rgba(0,0,0,.35)]">
                    <div class="rounded-[22px] bg-[#0a1325] p-3 sm:p-6">
                        <div class="flex flex-col gap-3 sm:gap-4">
                            <h3
                                class="mb-0 font-semibold"
                                style="font-family: 'Montserrat', sans-serif; font-size: 26px; line-height: 32px; letter-spacing: -0.576px; color: #007fff"
                            >
                                Glücksrad-Gewinne
                            </h3>
                            <p class="mb-0 font-semibold text-white" style="font-family: 'Montserrat', sans-serif; font-size: 19px; line-height: 140%">
                                Ein Sofortgewinn – direkt nach dem Dreh
                            </p>
                        </div>

                        <div class="mt-4 sm:mt-6 flex flex-col gap-2">
                            <div
                                v-for="prize in sofortgewinne"
                                :key="prize.title"
                                class="prize-card relative overflow-hidden flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-5 rounded-xl bg-white/[0.06] border border-white/20 transition-all duration-200 transform-gpu hover:scale-[1.01] hover:brightness-110 w-full max-w-full 2xl:max-w-[600px] sm:pl-2"
                            >
                                <span class="sofort-ribbon">SOFORT</span>
                                <img
                                    :src="prize.img"
                                    :alt="prize.title"
                                    class="prize-img self-center object-contain sm:shrink-0 px-4 pt-3 sm:p-0"
                                    :class="prize.imgClass ?? 'h-[120px] sm:self-auto sm:w-[140px] sm:h-[140px]'"
                                />
                                <div class="flex-1 min-w-0 px-3 sm:px-0 pb-3 sm:py-2.5 sm:mr-8 flex flex-col gap-2">
                                    <button
                                        class="w-full flex items-center justify-between gap-2 sm:cursor-default transition-transform duration-200 hover:scale-[1.01]"
                                        @click="toggleSofort(prize.title)"
                                    >
                                        <p class="font-semibold text-white text-sm leading-tight text-left sm:pr-32" style="hyphens: none">{{ prize.title }}</p>
                                        <svg
                                            class="sm:hidden shrink-0 transition-transform duration-200 text-white/60"
                                            :class="expandedSofort.has(prize.title) ? 'rotate-180' : ''"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path d="M2 5l6 6 6-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                        </svg>
                                    </button>
                                    <p class="text-xs text-white/90 leading-relaxed hidden sm:block">{{ prize.desc }}</p>
                                    <p v-if="expandedSofort.has(prize.title)" class="text-xs text-white/90 leading-relaxed sm:hidden">{{ prize.desc }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Hauptpreise (GOLD) -->
            <div class="flex flex-col items-stretch gap-4 w-full 2xl:w-1/2 2xl:min-w-0">
                <div v-if="props.landingPage" class="hidden 2xl:flex h-[320px] items-center justify-center overflow-visible -mt-6">
                    <img :src="imgPreisPraesentation" alt="HKCM Preise Präsentation" class="hero-img w-auto max-w-[680px] object-contain relative z-20" style="padding: 8px 16px 0 16px" />
                </div>
                <div class="w-full flex-1 rounded-[20px] p-3 sm:p-6 flex flex-col gap-0 relative" style="background: rgba(220, 193, 73, 0.3)">
                    <div class="flex flex-col gap-3 sm:gap-4">
                        <h3 class="text-yellow-300 mb-0 font-semibold" style="font-family: 'Montserrat', sans-serif; font-size: 26px; line-height: 32px; letter-spacing: -0.576px">
                            Hauptpreise
                        </h3>
                        <p class="mb-0 font-semibold text-white" style="font-family: 'Montserrat', sans-serif; font-size: 19px; line-height: 140%">Auslosung am Kampagnenende</p>
                    </div>

                    <div class="mt-4 sm:mt-6 flex flex-col gap-2">
                        <div
                            v-for="prize in hauptpreise"
                            :key="prize.title"
                            class="hauptpreis-card relative overflow-hidden flex flex-row rounded-xl w-full max-w-full 2xl:max-w-[600px] transition-all duration-200 transform-gpu hover:scale-[1.01] hover:brightness-110"
                            style="background: rgba(255, 255, 255, 0.1); border: 1px solid rgba(255, 255, 255, 0.2)"
                        >
                            <span class="auslosung-ribbon">AUSLOSUNG</span>
                            <div
                                v-if="prize.img"
                                class="w-[120px] sm:w-[140px] xl:w-[150px] h-auto shrink-0 overflow-hidden bg-[#10203b]"
                                style="filter: drop-shadow(0px 0px 30px #dcc149)"
                            >
                                <img :src="prize.img" :alt="prize.title" class="w-full h-full object-cover object-center" />
                            </div>
                            <div class="flex flex-col justify-center gap-2 py-5 px-5 flex-1 min-w-0">
                                <p class="font-semibold text-white text-sm leading-tight text-left pr-32">{{ prize.title }}</p>
                                <p class="mt-1 text-xs text-white/90 leading-relaxed">{{ prize.desc }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- So funktioniert's -->
        <section id="hkcm-wheel-so-funktioniert" class="relative mt-16 sm:mt-20 text-center" :class="props.landingPage ? '' : 'mx-1 sm:mx-2 md:mx-4'">
            <!-- Header -->
            <p
                style="
                    font-family: 'Montserrat', sans-serif;
                    font-weight: 600;
                    font-size: 18px;
                    line-height: 21px;
                    letter-spacing: 0.231px;
                    text-transform: uppercase;
                    color: #007fff;
                "
            >
                So funktioniert's
            </p>
            <p class="mt-2 hidden sm:block" style="font-family: 'Montserrat', sans-serif; font-weight: 600; font-size: 28px; line-height: 36px; color: #ececec">
                Einfach einkaufen, drehen und doppelt gewinnen!
            </p>

            <!-- Step-Indicator (Desktop) -->
            <div class="hidden xl:flex items-center justify-center mt-8 mb-2">
                <template v-for="(s, i) in steps" :key="'ind-' + s.title">
                    <div
                        class="w-[50px] h-[50px] rounded-full flex items-center justify-center shrink-0 font-bold text-xl"
                        :style="s.highlight ? 'background:#0071E3;color:#fff;' : 'background:#fff;color:#0071E3;'"
                    >
                        {{ i + 1 }}
                    </div>
                    <div v-if="i < steps.length - 1" class="h-[3px] w-[120px] xl:w-[200px]" :style="s.highlight ? 'background:#0071E3;' : 'background:#D9D9D9;'" />
                </template>
            </div>

            <!-- Karten -->
            <div class="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-5">
                <div
                    v-for="(s, i) in steps"
                    :key="s.title"
                    class="flex flex-col items-start rounded-xl w-full transition-all duration-200 transform-gpu xl:hover:scale-[1.01] xl:hover:brightness-110 p-4 sm:p-5"
                    :style="`gap:12px;border-radius:12px;background:rgba(255,255,255,0.1);border:1px solid ${s.highlight ? '#0071E3' : 'rgba(255,255,255,0.2)'};`"
                >
                    <div
                        class="w-[32px] h-[32px] rounded-full flex items-center justify-center text-sm font-bold xl:hidden"
                        :style="s.highlight ? 'background:#0071E3;color:#fff;' : 'background:#fff;color:#0071E3;'"
                    >
                        {{ i + 1 }}
                    </div>
                    <div class="flex flex-col items-center w-full gap-3">
                        <i
                            class="text-[50px] leading-none"
                            style="color: #0071e3"
                            :class="{
                                'fi fi-rr-shopping-cart': s.icon === 'cart',
                                'fi fi-rr-refresh': s.icon === 'spin',
                                'fi fi-rr-trophy': s.icon === 'trophy',
                                'fi fi-rr-ticket': s.icon === 'ticket'
                            }"
                        ></i>
                        <p
                            class="text-center font-semibold w-full"
                            style="font-family: 'Montserrat', sans-serif; font-size: 18px; line-height: 28px; letter-spacing: -0.144px; color: #ececec"
                        >
                            {{ s.title }}
                        </p>
                    </div>
                    <p class="text-center w-full" style="font-family: 'Montserrat', sans-serif; font-size: 15px; line-height: 140%; font-weight: 400; color: #fff">
                        {{ s.subtitle }}
                    </p>
                </div>
            </div>

            <p class="mt-6 text-xxs text-white">
                DISCLAIMER: Für die HKCM-Glücksrad-Aktion gelten folgende
                <router-link to="/verlosung" class="text-[#dcc149] underline">Teilnahmebedingungen</router-link> und
                <router-link to="/verlosungdatenschutz" class="text-[#dcc149] underline">Datenschutzhinweise</router-link>.
            </p>
            <p class="mt-3 text-xxs text-white">
                **Auch Upgrades zählen: Wenn Sie ein bestehendes Produkt auf eine höhere Laufzeit upgraden und der zusätzliche Warenkorbwert mindestens
                {{ formatOrderValue(props.minOrderValue) }} € beträgt, qualifiziert sich auch dieses Upgrade für eine weitere Drehung.
            </p>

            <!-- CTA Footer -->
            <div
                class="relative overflow-visible mt-8 flex flex-col lg:flex-row items-start justify-between gap-5 rounded-2xl px-4 sm:px-6 py-5 w-full"
                style="
                    background: linear-gradient(135deg, rgba(220, 193, 73, 0.18) 0%, rgba(220, 193, 73, 0.08) 100%);
                    border: 1px solid rgba(220, 193, 73, 0.6);
                    box-shadow: 0 0 40px rgba(220, 193, 73, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.08);
                "
            >
                <div class="flex flex-col gap-3 flex-1 min-w-0">
                    <p style="font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 17px; line-height: 130%; color: #dcc149">
                        Exklusive 20&nbsp;% Zusatzrabatt auf alle Produkte mit dem Code: <span style="white-space: nowrap">GLUECK20</span>
                    </p>
                    <p style="font-family: 'Montserrat', sans-serif; font-weight: 400; font-size: 15px; line-height: 150%; color: #fff">
                        Während des Aktionszeitraums erhalten alle Käufer unserer Produkte zusätzlich 20&nbsp;% Rabatt auf das gesamte HKCM-Sortiment, inklusive Videoanalysen.<br />
                        Der Rabatt gilt ausschließlich bei Eingabe des Rabatt-Codes <span style="color: #dcc149; font-weight: 700">GLUECK20</span> im Checkout. Der Code ist nicht automatisch hinterlegt und nur während des Aktionszeitraums (28.05.–08.06.2026) gültig.
                    </p>
                </div>
                <img
                    :src="imgPHPointer"
                    alt=""
                    aria-hidden="true"
                    class="hidden lg:block self-end shrink-0 object-contain pointer-events-none rotate-[-3deg]"
                    style="width: 150px; height: auto; margin-bottom: -30px; filter: drop-shadow(10px 14px 14px rgba(0, 0, 0, 0.35))"
                />
                <div class="w-full lg:w-auto flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-stretch gap-3 shrink-0 justify-center lg:self-center">
                    <button
                        class="group flex items-center justify-center gap-2.5 px-4 py-2 rounded-xl transition-all duration-200 hover:scale-[1.01] active:scale-95 w-full sm:w-auto"
                        style="background: rgba(220, 193, 73, 0.15); border: 1px solid rgba(220, 193, 73, 0.5); box-shadow: 0 2px 12px rgba(220, 193, 73, 0.15)"
                        @click="copyCode"
                    >
                        <span style="font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 17px; color: #dcc149; letter-spacing: 2px">GLUECK20</span>
                        <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#DCC149" viewBox="0 0 256 256" class="opacity-80 group-hover:opacity-100 transition-opacity">
                            <path d="M216,32H88a8,8,0,0,0-8,8V80H40a8,8,0,0,0-8,8V216a8,8,0,0,0,8,8H168a8,8,0,0,0,8-8V176h40a8,8,0,0,0,8-8V40A8,8,0,0,0,216,32ZM160,208H48V96H160Zm48-48H176V88a8,8,0,0,0-8-8H96V48H208Z"></path>
                        </svg>
                        <span v-else style="font-size: 13px; color: #4ade80; font-weight: 600">✓ Kopiert</span>
                    </button>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
@media (min-width: 2536px) {
    .hide-1636 {
        display: none !important;
    }
    .mt-reset-1636 {
        margin-top: 0 !important;
    }
}

.prize-img {
    filter: drop-shadow(0 0 18px rgba(59, 130, 246, 0.7));
    transition: filter 0.25s ease;
}
.prize-card:hover .prize-img {
    filter: drop-shadow(0 0 30px rgba(59, 130, 246, 1));
}

.hero-img {
    filter: drop-shadow(0 0 20px rgba(220, 193, 73, 0.5));
}

.sofort-ribbon,
.auslosung-ribbon {
    position: absolute;
    right: -35px;
    top: 17px;
    width: 165px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: rotate(30deg);
    transform-origin: center;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    letter-spacing: 0.5px;
    text-align: center;
    white-space: nowrap;
    color: #fff;
    z-index: 1;
    padding: 0;}

.sofort-ribbon {
    background: #007fff;
}
.auslosung-ribbon {
    background: #dcc149;
    color: #007fff;
}

.cta-gold-shine::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(120deg, transparent 30%, rgba(255, 244, 187, 0.05) 44%, rgba(255, 244, 187, 0.16) 50%, rgba(255, 244, 187, 0.05) 56%, transparent 70%);
    transform: translateX(-120%);
    animation: goldShineSweep 7.2s ease-in-out infinite;
}

.cta-gold-shine {
    box-shadow:
        0 0 20px rgba(220, 193, 73, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.07);
    animation: goldGlowPulse 6.4s ease-in-out infinite;
}

@keyframes goldShineSweep {
    0% {
        transform: translateX(-120%);
        opacity: 0;
    }
    18% {
        opacity: 0.45;
    }
    42% {
        transform: translateX(120%);
        opacity: 0;
    }
    100% {
        transform: translateX(120%);
        opacity: 0;
    }
}

@keyframes goldGlowPulse {
    0%,
    100% {
        box-shadow:
            0 0 18px rgba(220, 193, 73, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }
    50% {
        box-shadow:
            0 0 24px rgba(220, 193, 73, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.08);
    }
}

@media (prefers-reduced-motion: reduce) {
    .cta-gold-shine,
    .cta-gold-shine::before {
        animation: none !important;
    }
}

@media (max-width: 640px) {
    .sofort-ribbon,
    .auslosung-ribbon {
        right: -22px;
        top: 10px;
        width: 100px;
        height: 18px;
        font-size: 8px;
        letter-spacing: 0.3px;
    
    }
}
</style>
